import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Empty-return deadline countdown and escalation (PRD → Job intake → empty-return deadline;
 * gitaumoses4/chukua#38). The deadline itself is recorded on the container job (#31); these are
 * the escalation levels an admin configures, the "empty returned" action, and the deadlines board.
 */

/** Compared with today's UTC date, so in the first hours of a local day ahead of UTC a return dated "today" is still refused. */
function isNotFuture(isoDate: string): boolean {
  return isoDate <= new Date().toISOString().slice(0, 10);
}

export const emptyReturnInputSchema = z.object({
  returnedOn: z.iso.date().refine(isNotFuture, 'An empty cannot have been returned in the future'),
});
export type EmptyReturnInput = z.infer<typeof emptyReturnInputSchema>;

/** Negative means overdue — "Overdue" is just a level whose threshold is below zero. */
export const escalationLevelInputSchema = z.object({
  label: z.string().trim().min(1).max(60),
  daysThreshold: z.number().int().min(-365).max(365),
});
export type EscalationLevelInput = z.infer<typeof escalationLevelInputSchema>;

export interface EscalationLevelView {
  id: string;
  label: string;
  daysThreshold: number;
  isActive: boolean;
}

/** `GET /deadlines` — search matches the container number, job number or customer name; sorting by `deadline` is sorting by days remaining. The customer's name lives in another module, so it can be searched but not sorted. */
export const emptyReturnBoardListQuerySchema = defineListQuery({
  sortFields: ['deadline', 'containerNumber'],
  defaultSort: { sort: 'deadline', order: 'asc' },
  filters: {
    customerId: listFilter(z.string().uuid()),
    levelId: listFilter(z.string().uuid()),
  },
});
export type EmptyReturnBoardListQuery = z.infer<typeof emptyReturnBoardListQuerySchema>;

export interface EmptyReturnBoardItem {
  containerId: string;
  containerNumber: string;
  jobId: string;
  jobNumber: string;
  customerId: string;
  customerName: string;
  depotZoneCode: string;
  deadline: string;
  daysRemaining: number;
  level: { id: string; label: string; daysThreshold: number } | null;
}
